import React, { useEffect, useState } from 'react';
import { DocumentViewer } from './DocumentViewer';
import { ExceptionQueue } from './ExceptionQueue';
import { DynamicDataEditor } from './DynamicDataEditor';
import { NonPOInvoiceForm } from './NonPOInvoiceForm';
import { VendorLookupModal } from '../vendor/VendorLookupModal';
import { OrgUnitSelector } from '../settings/orgunit/OrgUnitSelector';
import { flattenOrgUnits, sampleOrgUnits, type OrgUnit } from '../financial/sharedFinancialData';
import { exceptionDocuments, ExceptionDocument } from './sharedTaskData';
import { AlertTriangleIcon, CheckCircleIcon, RotateCwIcon, SaveIcon, FlagIcon, CornerDownRightIcon, ChevronDownIcon, ChevronUpIcon, PlusIcon, ThumbsUpIcon, ThumbsDownIcon, ClockIcon, CheckIcon, FileTextIcon } from 'lucide-react';
// Sample document image URL
const sampleDocumentUrl = 'https://templates.invoicehome.com/invoice-template-us-neat-750px.png';
interface DIExceptionHandlingProps {
  locale?: string;
  initialExceptionId?: string;
}
export function DIExceptionHandling({
  locale = 'en-US',
  initialExceptionId
}: DIExceptionHandlingProps) {
  const [exceptions, setExceptions] = useState<ExceptionDocument[]>(exceptionDocuments);
  const [selectedException, setSelectedException] = useState<ExceptionDocument | null>(null);
  const [documentType, setDocumentType] = useState('PO Invoice');
  const [documentImage, setDocumentImage] = useState(sampleDocumentUrl);
  const [isLoading, setIsLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [expandedSections, setExpandedSections] = useState({
    header: true,
    vendor: true,
    entitySite: true,
    lineItems: true
  });
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isVendorLookupOpen, setIsVendorLookupOpen] = useState(false);
  // Get all OrgUnits from shared data
  const allOrgUnits = flattenOrgUnits(sampleOrgUnits);
  // Add state for extracted bank details and verification
  const [extractedBankDetails, setExtractedBankDetails] = useState({
    accountName: 'PrintWorks Inc.',
    accountNumber: '4567890123',
    routingNumber: '021000021',
    bankName: 'Chase Bank',
    swiftCode: 'CHASUS33',
    extractedFrom: 'INV-2023-4501',
    extractionDate: '2023-10-15'
  });
  // Add state for EFTSure verification status
  const [eftSureVerification, setEftSureVerification] = useState({
    status: 'mismatch',
    verifiedDate: '2023-10-15',
    mismatchFields: ['accountNumber', 'accountName']
  });
  // Initialize with exception from task if provided
  useEffect(() => {
    if (initialExceptionId) {
      const exception = exceptions.find(exc => exc.id === initialExceptionId);
      if (exception) {
        handleSelectException(exception);
      }
    }
  }, [initialExceptionId]);
  // Toggle section expansion
  const toggleSection = section => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  // Get confidence level class
  const getConfidenceClass = confidence => {
    if (confidence >= 0.8) return 'bg-green-100 text-green-800';
    if (confidence >= 0.6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };
  // Render confidence indicator
  const renderConfidenceIndicator = confidence => {
    if (confidence === undefined) return null;
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getConfidenceClass(confidence)}`} data-id="element-4107">
        {Math.round(confidence * 100)}%
      </span>;
  };
  // Sample PO data for matching
  const [availablePOs, setAvailablePOs] = useState([{
    id: 'PO-2023-001',
    vendor: 'PrintWorks Inc.',
    date: '2023-09-20',
    total: 3450.0,
    lines: [{
      id: 'line-001',
      description: 'Marketing Brochures (Glossy)',
      quantity: 5000,
      unitPrice: 0.45,
      total: 2250.0,
      taxCode: 'STD'
    }, {
      id: 'line-002',
      description: 'Business Cards',
      quantity: 1000,
      unitPrice: 0.15,
      total: 150.0,
      taxCode: 'STD'
    }, {
      id: 'line-003',
      description: 'Large Format Posters',
      quantity: 50,
      unitPrice: 21.0,
      total: 1050.0,
      taxCode: 'STD'
    }]
  }, {
    id: 'PO-2023-002',
    vendor: 'Hardware Supplies Inc.',
    date: '2023-09-15',
    total: 1850.25,
    lines: [{
      id: 'line-001',
      description: 'Office Chairs',
      quantity: 5,
      unitPrice: 250.0,
      total: 1250.0,
      taxCode: 'STD'
    }, {
      id: 'line-002',
      description: 'Desk Lamps',
      quantity: 10,
      unitPrice: 45.5,
      total: 455.0,
      taxCode: 'STD'
    }, {
      id: 'line-003',
      description: 'Filing Cabinets',
      quantity: 2,
      unitPrice: 72.625,
      total: 145.25,
      taxCode: 'STD'
    }]
  }]);
  // Sample invoice data for credit note matching
  const [availableInvoices, setAvailableInvoices] = useState([{
    id: 'INV-2023-001',
    vendor: 'Tech Solutions',
    date: '2023-09-18',
    total: 2500.0,
    lines: [{
      id: 'line-001',
      description: 'Software License - Premium',
      quantity: 5,
      unitPrice: 400.0,
      total: 2000.0,
      taxCode: 'STD'
    }, {
      id: 'line-002',
      description: 'Support Hours',
      quantity: 10,
      unitPrice: 50.0,
      total: 500.0,
      taxCode: 'STD'
    }]
  }, {
    id: 'INV-2023-002',
    vendor: 'Tech Solutions',
    date: '2023-09-10',
    total: 1750.0,
    lines: [{
      id: 'line-001',
      description: 'Hardware Installation',
      quantity: 1,
      unitPrice: 1500.0,
      total: 1500.0,
      taxCode: 'STD'
    }, {
      id: 'line-002',
      description: 'Training Session',
      quantity: 5,
      unitPrice: 50.0,
      total: 250.0,
      taxCode: 'STD'
    }]
  }]);
  // Sample data for GL codes, cost centers, etc.
  const [availableGLCodes, setAvailableGLCodes] = useState([{
    code: '5001',
    description: 'Office Supplies'
  }, {
    code: '5002',
    description: 'IT Equipment'
  }, {
    code: '5003',
    description: 'Marketing Expenses'
  }, {
    code: '5004',
    description: 'Travel Expenses'
  }, {
    code: '5005',
    description: 'Professional Services'
  }, {
    code: '5006',
    description: 'Utilities'
  }, {
    code: '5007',
    description: 'Rent'
  }, {
    code: '5008',
    description: 'Insurance'
  }, {
    code: '5009',
    description: 'Training & Development'
  }, {
    code: '5010',
    description: 'Miscellaneous Expenses'
  }]);
  // Sample data for cost centers
  const [availableCostCenters, setAvailableCostCenters] = useState([{
    code: 'CC-001',
    description: 'IT Department'
  }, {
    code: 'CC-002',
    description: 'Marketing Department'
  }, {
    code: 'CC-003',
    description: 'Finance Department'
  }, {
    code: 'CC-004',
    description: 'HR Department'
  }, {
    code: 'CC-005',
    description: 'Operations'
  }, {
    code: 'CC-006',
    description: 'Executive'
  }, {
    code: 'CC-007',
    description: 'Sales'
  }, {
    code: 'CC-008',
    description: 'Customer Support'
  }]);
  // Sample extracted data
  const [extractedData, setExtractedData] = useState({
    documentType: 'PO Invoice',
    header: {
      invoiceNumber: 'INV-2023-4501',
      invoiceDate: '2023-10-05',
      dueDate: '2023-11-05',
      poReference: 'PO-2023-001',
      currency: 'USD',
      total: 3200.5,
      subtotal: 2965.0,
      tax: 235.5
    },
    vendor: {
      id: 'VEN-001',
      name: 'PrintWorks Inc.',
      address: '123 Business Ave, Suite 100, San Francisco, CA 94107',
      taxId: 'US-123456789',
      email: 'invoices@printworks.com'
    },
    orgUnit: {
      orgUnitId: 'ou-hq',
      orgUnitName: 'Headquarters',
      orgUnitPath: 'Global Corp / North America / Headquarters'
    },
    lineItems: [{
      id: 1,
      description: 'Marketing Brochures (Glossy)',
      quantity: 5000,
      unitPrice: 0.45,
      total: 2250.0,
      taxCode: 'STD',
      taxAmount: 180.0,
      matchedTo: null,
      glCode: null,
      costCenter: null,
      orgUnit: null
    }, {
      id: 2,
      description: 'Business Cards',
      quantity: 1000,
      unitPrice: 0.15,
      total: 150.0,
      taxCode: 'STD',
      taxAmount: 12.0,
      matchedTo: null,
      glCode: null,
      costCenter: null,
      orgUnit: null
    }, {
      id: 3,
      description: 'Large Format Posters',
      quantity: 55,
      unitPrice: 21.0,
      total: 1155.0,
      taxCode: 'STD',
      taxAmount: 92.4,
      matchedTo: null,
      glCode: null,
      costCenter: null,
      orgUnit: null
    }],
    selectedPOs: [],
    selectedInvoices: [],
    confidence: {
      documentType: 0.65,
      invoiceNumber: 0.88,
      invoiceDate: 0.92,
      dueDate: 0.75,
      poReference: 0.85,
      vendorName: 0.82,
      vendorAddress: 0.65,
      total: 0.9,
      subtotal: 0.85,
      tax: 0.78,
      currency: 0.95,
      lineItems: [0.88, 0.92, 0.75],
      orgUnitName: 0.7
    },
    bankDetails: {
      verified: true,
      mismatchFields: []
    }
  });
  // Load document when selecting an exception
  const handleSelectException = exception => {
    setIsLoading(true);
    setSelectedException(exception);
    setIsDirty(false);
    setCurrentPageIndex(0); // Reset to first page when selecting a new document
    // Simulate API call to fetch document data
    setTimeout(() => {
      // Set document type based on detected type or default to PO Invoice
      setDocumentType(exception.detectedType || 'PO Invoice');
      // Set document image to the first page's image
      if (exception.pages && exception.pages.length > 0) {
        setDocumentImage(exception.pages[0].fullImage);
      } else {
        setDocumentImage(sampleDocumentUrl);
      }
      // Update the extractedData with the selected exception's vendor and bank details
      setExtractedData({
        ...extractedData,
        vendor: {
          ...extractedData.vendor,
          name: exception.vendor
        },
        header: {
          ...extractedData.header,
          invoiceDate: exception.date,
          total: exception.amount
        },
        documentType: exception.detectedType || 'PO Invoice',
        bankDetails: {
          verified: exception.id === 'exc-001' ? false : exception.id === 'exc-002' ? true : null,
          mismatchFields: exception.id === 'exc-001' ? ['accountNumber', 'accountName'] : []
        }
      });
      setIsLoading(false);
    }, 800);
  };
  // Handle moving a page between documents
  const handleMovePage = (sourceDocId, pageIndex) => {
    // In a real app, this would open a modal to select the target document
    alert(`Moving page ${pageIndex + 1} from document ${sourceDocId} to another document. This would open a selection dialog in a real application.`);
  };
  // Handle splitting a document at a specific page
  const handleSplitDocument = (documentId, pageIndex) => {
    alert(`Splitting document ${documentId} at page ${pageIndex + 1}. This would create a new document in a real application.`);
  };
  // Change the current page being viewed
  const handlePageChange = pageIndex => {
    if (selectedException && selectedException.pages && selectedException.pages[pageIndex]) {
      setCurrentPageIndex(pageIndex);
      setDocumentImage(selectedException.pages[pageIndex].fullImage);
    }
  };
  // Handle document type change
  const handleDocumentTypeChange = type => {
    setDocumentType(type);
    setIsDirty(true);
    // Update the extracted data document type
    setExtractedData({
      ...extractedData,
      documentType: type
    });
  };
  // Handle field updates
  const handleFieldUpdate = (section, field, value) => {
    setExtractedData(prevData => ({
      ...prevData,
      [section]: {
        ...prevData[section],
        [field]: value
      }
    }));
    setIsDirty(true);
  };
  // Handle line item updates
  const handleLineItemUpdate = (index, field, value) => {
    setExtractedData(prevData => {
      const updatedLineItems = [...prevData.lineItems];
      updatedLineItems[index] = {
        ...updatedLineItems[index],
        [field]: value
      };
      return {
        ...prevData,
        lineItems: updatedLineItems
      };
    });
    setIsDirty(true);
  };
  // Handle PO selection
  const handlePOSelection = (poId, selected) => {
    setExtractedData(prevData => {
      let updatedSelectedPOs = [...prevData.selectedPOs];
      if (selected) {
        const poToAdd = availablePOs.find(po => po.id === poId);
        if (poToAdd && !updatedSelectedPOs.some(po => po.id === poId)) {
          updatedSelectedPOs.push(poToAdd);
        }
      } else {
        updatedSelectedPOs = updatedSelectedPOs.filter(po => po.id !== poId);
      }
      return {
        ...prevData,
        selectedPOs: updatedSelectedPOs
      };
    });
    setIsDirty(true);
  };
  // Handle invoice selection for credit notes
  const handleInvoiceSelection = (invoiceId, selected) => {
    setExtractedData(prevData => {
      let updatedSelectedInvoices = [...prevData.selectedInvoices];
      if (selected) {
        const invoiceToAdd = availableInvoices.find(inv => inv.id === invoiceId);
        if (invoiceToAdd && !updatedSelectedInvoices.some(inv => inv.id === invoiceId)) {
          updatedSelectedInvoices.push(invoiceToAdd);
        }
      } else {
        updatedSelectedInvoices = updatedSelectedInvoices.filter(inv => inv.id !== invoiceId);
      }
      return {
        ...prevData,
        selectedInvoices: updatedSelectedInvoices
      };
    });
    setIsDirty(true);
  };
  // Handle line matching
  const handleLineMatching = (lineIndex, matchedLineId, poId) => {
    setExtractedData(prevData => {
      const updatedLineItems = [...prevData.lineItems];
      updatedLineItems[lineIndex] = {
        ...updatedLineItems[lineIndex],
        matchedTo: {
          lineId: matchedLineId,
          poId: poId
        }
      };
      return {
        ...prevData,
        lineItems: updatedLineItems
      };
    });
    setIsDirty(true);
  };
  // Handle save draft
  const handleSaveDraft = () => {
    setIsLoading(true);
    // Simulate API call to save draft
    setTimeout(() => {
      // Update exception status to "In Progress"
      if (selectedException) {
        setExceptions(exceptions.map(exc => exc.id === selectedException.id ? {
          ...exc,
          status: 'In Progress',
          assignedTo: 'current.user@example.com'
        } : exc));
        setSelectedException({
          ...selectedException,
          status: 'In Progress',
          assignedTo: 'current.user@example.com'
        });
      }
      setIsLoading(false);
      setIsDirty(false);
      alert('Draft saved successfully');
    }, 800);
  };
  // Handle validation
  const handleValidate = () => {
    setIsLoading(true);
    // Simulate API call to validate
    setTimeout(() => {
      // Remove the exception from the list
      setExceptions(exceptions.filter(exc => exc.id !== selectedException.id));
      setSelectedException(null);
      setIsLoading(false);
      setIsDirty(false);
      alert('Document validated and sent for processing');
    }, 800);
  };
  // Handle re-extraction
  const handleReExtract = () => {
    setIsLoading(true);
    // Simulate API call to re-extract
    setTimeout(() => {
      // Just simulate a refresh
      setIsLoading(false);
      alert('Document re-extracted successfully');
    }, 1200);
  };
  // Handle flag exception
  const handleFlagException = () => {
    setIsLoading(true);
    // Simulate API call to flag exception
    setTimeout(() => {
      // Update exception status
      if (selectedException) {
        setExceptions(exceptions.map(exc => exc.id === selectedException.id ? {
          ...exc,
          status: 'Waiting Approval'
        } : exc));
        setSelectedException({
          ...selectedException,
          status: 'Waiting Approval'
        });
      }
      setIsLoading(false);
      setIsDirty(false);
      alert('Exception flagged for further review');
    }, 800);
  };
  // Filter exceptions based on selected filters
  const filteredExceptions = exceptions.filter(exception => {
    const matchesStatus = statusFilter === 'All' || exception.status === statusFilter;
    const matchesType = typeFilter === 'All' || exception.detectedType === typeFilter;
    return matchesStatus && matchesType;
  });
  // Render PO Invoice Line Items
  const renderPOInvoiceLineItems = () => {
    return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-4108">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-4109">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-4110">
            Line Items & PO Matching
          </h3>
          <div className="flex space-x-2" data-id="element-4111">
            <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-4112">
              Auto-match
            </button>
            <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-4113">
              Add Line
            </button>
          </div>
        </div>
        {/* PO Selection Section */}
        <div className="p-4 border-b border-gray-200" data-id="element-4114">
          <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-4115">
            Select Purchase Order(s)
          </h5>
          <div className="bg-gray-50 p-3 rounded-md border border-gray-200" data-id="element-4116">
            {availablePOs.length === 0 ? <p className="text-sm text-gray-500" data-id="element-4117">
                No purchase orders available for this vendor
              </p> : <div className="space-y-2" data-id="element-4118">
                {availablePOs.map(po => <div key={po.id} className="border border-gray-200 rounded-md bg-white" data-id="element-4119">
                    <div className="flex items-center justify-between p-3" data-id="element-4120">
                      <div className="flex items-center" data-id="element-4121">
                        <input type="checkbox" id={`po-${po.id}`} checked={extractedData.selectedPOs.some(selectedPO => selectedPO.id === po.id)} onChange={e => handlePOSelection(po.id, e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-4122" />
                        <label htmlFor={`po-${po.id}`} className="ml-3 text-sm font-medium text-gray-700" data-id="element-4123">
                          {po.id}
                        </label>
                      </div>
                      <div className="flex items-center space-x-4" data-id="element-4124">
                        <div className="text-sm text-gray-500" data-id="element-4125">{po.date}</div>
                        <div className="text-sm font-medium text-gray-900" data-id="element-4126">
                          {new Intl.NumberFormat(locale, {
                      style: 'currency',
                      currency: 'USD'
                    }).format(po.total)}
                        </div>
                        <button type="button" onClick={() => {
                    setExpandedSections({
                      ...expandedSections,
                      [`po-${po.id}`]: !expandedSections[`po-${po.id}`]
                    });
                  }} className="text-gray-400 hover:text-gray-500" data-id="element-4127">
                          {expandedSections[`po-${po.id}`] ? <ChevronUpIcon className="h-5 w-5" data-id="element-4128" /> : <ChevronDownIcon className="h-5 w-5" data-id="element-4129" />}
                        </button>
                      </div>
                    </div>
                    {expandedSections[`po-${po.id}`] && <div className="border-t border-gray-200 p-3" data-id="element-4130">
                        <div className="overflow-x-auto" data-id="element-4131">
                          <table className="min-w-full divide-y divide-gray-200" data-id="element-4132">
                            <thead className="bg-gray-50" data-id="element-4133">
                              <tr data-id="element-4134">
                                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4135">
                                  Description
                                </th>
                                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4136">
                                  Qty
                                </th>
                                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4137">
                                  Unit Price
                                </th>
                                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4138">
                                  Total
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200" data-id="element-4139">
                              {po.lines.map(line => <tr key={line.id} data-id="element-4140">
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900" data-id="element-4141">
                                    {line.description}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-4142">
                                    {line.quantity}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-4143">
                                    {new Intl.NumberFormat(locale, {
                            style: 'currency',
                            currency: 'USD'
                          }).format(line.unitPrice)}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900" data-id="element-4144">
                                    {new Intl.NumberFormat(locale, {
                            style: 'currency',
                            currency: 'USD'
                          }).format(line.total)}
                                  </td>
                                </tr>)}
                            </tbody>
                          </table>
                        </div>
                      </div>}
                  </div>)}
              </div>}
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-4145">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-4146">
            <thead className="bg-gray-50" data-id="element-4147">
              <tr data-id="element-4148">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4149">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4150">
                  Quantity
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4151">
                  Unit Price
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4152">
                  Tax Code
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4153">
                  Total
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4154">
                  Matched PO Line
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4155">
                  Variance
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4156">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-4157">
              {extractedData.lineItems.map((item, index) => <tr key={item.id} className={item.matchedTo ? '' : 'bg-red-50'} data-id="element-4158">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4159">
                    <input type="text" value={item.description} onChange={e => handleLineItemUpdate(index, 'description', e.target.value)} className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-4160" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4161">
                    <input type="number" value={item.quantity} onChange={e => handleLineItemUpdate(index, 'quantity', parseInt(e.target.value))} className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-4162" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4163">
                    <input type="number" step="0.01" value={item.unitPrice} onChange={e => handleLineItemUpdate(index, 'unitPrice', parseFloat(e.target.value))} className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-4164" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4165">
                    <select value={item.taxCode} onChange={e => handleLineItemUpdate(index, 'taxCode', e.target.value)} className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-4166">
                      <option value="STD" data-id="element-4167">STD</option>
                      <option value="ZERO" data-id="element-4168">ZERO</option>
                      <option value="EXEMPT" data-id="element-4169">EXEMPT</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4170">
                    {new Intl.NumberFormat(locale, {
                  style: 'currency',
                  currency: extractedData.header.currency || 'USD'
                }).format(item.total)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4171">
                    <select value={item.matchedTo ? `${item.matchedTo.poId}:${item.matchedTo.lineId}` : ''} onChange={e => {
                  if (e.target.value) {
                    const [poId, lineId] = e.target.value.split(':');
                    handleLineMatching(index, lineId, poId);
                  } else {
                    handleLineMatching(index, null, null);
                  }
                }} className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-4172">
                      <option value="" data-id="element-4173">Select PO Line</option>
                      {extractedData.selectedPOs.map(po => po.lines.map(line => <option key={`${po.id}:${line.id}`} value={`${po.id}:${line.id}`} data-id="element-4174">
                            {po.id} - {line.description} ({line.quantity} @{' '}
                            {line.unitPrice})
                          </option>))}
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4175">
                    {item.matchedTo ? <span className={item.total !== item.matchedTo.total ? 'text-red-600 font-semibold' : 'text-green-600'} data-id="element-4176">
                        {new Intl.NumberFormat(locale, {
                    style: 'currency',
                    currency: extractedData.header.currency || 'USD',
                    signDisplay: 'always'
                  }).format(item.total - (item.matchedTo ? item.matchedTo.total : 0))}
                      </span> : <span className="text-gray-400" data-id="element-4177">-</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4178">
                    <button onClick={() => handleLineItemUpdate(index, 'matchedTo', null)} className="text-red-600 hover:text-red-900" data-id="element-4179">
                      Clear
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>;
  };
  // Render Non-PO Invoice Line Items
  const renderNonPOInvoiceLineItems = () => {
    return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-4180">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-4181">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-4182">
            Line Items & GL Coding
          </h3>
          <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-4183">
            Add Line
          </button>
        </div>
        <div className="overflow-x-auto" data-id="element-4184">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-4185">
            <thead className="bg-gray-50" data-id="element-4186">
              <tr data-id="element-4187">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4188">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4189">
                  Quantity
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4190">
                  Unit Price
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4191">
                  Tax Code
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4192">
                  GL Code
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4193">
                  Cost Center
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4194">
                  Entity/Site
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4195">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-4196">
              {extractedData.lineItems.map((item, index) => <tr key={item.id} className={!item.glCode ? 'bg-red-50' : ''} data-id="element-4197">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4198">
                    <input type="text" value={item.description} onChange={e => handleLineItemUpdate(index, 'description', e.target.value)} className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-4199" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4200">
                    <input type="number" value={item.quantity} onChange={e => handleLineItemUpdate(index, 'quantity', parseInt(e.target.value))} className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-4201" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4202">
                    <input type="number" step="0.01" value={item.unitPrice} onChange={e => handleLineItemUpdate(index, 'unitPrice', parseFloat(e.target.value))} className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-4203" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4204">
                    <select value={item.taxCode} onChange={e => handleLineItemUpdate(index, 'taxCode', e.target.value)} className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-4205">
                      <option value="STD" data-id="element-4206">STD</option>
                      <option value="ZERO" data-id="element-4207">ZERO</option>
                      <option value="EXEMPT" data-id="element-4208">EXEMPT</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4209">
                    <select value={item.glCode || ''} onChange={e => handleLineItemUpdate(index, 'glCode', e.target.value)} className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-4210">
                      <option value="" data-id="element-4211">Select GL Code</option>
                      {availableGLCodes.map(gl => <option key={gl.code} value={gl.code} data-id="element-4212">
                          {gl.code} - {gl.description}
                        </option>)}
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4213">
                    <select value={item.costCenter || ''} onChange={e => handleLineItemUpdate(index, 'costCenter', e.target.value)} className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-4214">
                      <option value="" data-id="element-4215">Select Cost Center</option>
                      {availableCostCenters.map(cc => <option key={cc.code} value={cc.code} data-id="element-4216">
                          {cc.code} - {cc.description}
                        </option>)}
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4217">
                    <select value={item.entity || ''} onChange={e => handleLineItemUpdate(index, 'entity', e.target.value)} className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-4218">
                      <option value="" data-id="element-4219">Select Entity/Site</option>
                      {allOrgUnits.map(orgUnit => <option key={orgUnit.id} value={orgUnit.id} data-id="element-4220">
                          {orgUnit.name} ({orgUnit.path})
                        </option>)}
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4221">
                    {new Intl.NumberFormat(locale, {
                  style: 'currency',
                  currency: extractedData.header.currency || 'USD'
                }).format(item.total)}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>;
  };
  // Render Credit Note Line Items
  const renderCreditNoteLineItems = () => {
    return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-4222">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-4223">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-4224">
            Line Items & Invoice Allocation
          </h3>
          <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-4225">
            Add Line
          </button>
        </div>
        <div className="overflow-x-auto" data-id="element-4226">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-4227">
            <thead className="bg-gray-50" data-id="element-4228">
              <tr data-id="element-4229">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4230">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4231">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4232">
                  Tax Code
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4233">
                  Matched Invoice
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4234">
                  Allocated Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4235">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-4236">
              {extractedData.lineItems.map((item, index) => <tr key={item.id} data-id="element-4237">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4238">
                    <input type="text" value={item.description} onChange={e => handleLineItemUpdate(index, 'description', e.target.value)} className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-4239" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4240">
                    <div className="relative rounded-md shadow-sm" data-id="element-4241">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4242">
                        <span className="text-gray-500 sm:text-sm" data-id="element-4243">
                          {extractedData.header.currency === 'USD' ? '$' : extractedData.header.currency}
                        </span>
                      </div>
                      <input type="number" step="0.01" value={item.total.toFixed(2)} onChange={e => handleLineItemUpdate(index, 'total', parseFloat(e.target.value))} className="block w-full pl-7 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-4244" />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4245">
                    <select value={item.taxCode} onChange={e => handleLineItemUpdate(index, 'taxCode', e.target.value)} className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-4246">
                      <option value="STD" data-id="element-4247">STD</option>
                      <option value="ZERO" data-id="element-4248">ZERO</option>
                      <option value="EXEMPT" data-id="element-4249">EXEMPT</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4250">
                    <select value={item.matchedTo ? item.matchedTo.invoiceId : ''} onChange={e => {
                  if (e.target.value) {
                    handleLineMatching(index, e.target.value, null);
                  } else {
                    handleLineMatching(index, null, null);
                  }
                }} className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-4251">
                      <option value="" data-id="element-4252">Select Invoice</option>
                      {availableInvoices.map(invoice => <option key={invoice.id} value={invoice.id} data-id="element-4253">
                          {invoice.id} - {invoice.date} (
                          {new Intl.NumberFormat(locale, {
                      style: 'currency',
                      currency: extractedData.header.currency || 'USD'
                    }).format(invoice.total)}
                          )
                        </option>)}
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4254">
                    <div className="relative rounded-md shadow-sm" data-id="element-4255">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4256">
                        <span className="text-gray-500 sm:text-sm" data-id="element-4257">
                          {extractedData.header.currency === 'USD' ? '$' : extractedData.header.currency}
                        </span>
                      </div>
                      <input type="number" step="0.01" value={(item.allocatedAmount || item.total).toFixed(2)} onChange={e => handleLineItemUpdate(index, 'allocatedAmount', parseFloat(e.target.value))} className="block w-full pl-7 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" disabled={!item.matchedTo} data-id="element-4258" />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4259">
                    <button onClick={() => handleLineItemUpdate(index, 'matchedTo', null)} className="text-red-600 hover:text-red-900" disabled={!item.matchedTo} data-id="element-4260">
                      Clear
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>;
  };
  // Render Header & Vendor Information
  const renderHeaderAndVendorInfo = () => {
    return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-4261">
        {/* Document Classification */}
        <div className="px-4 py-4 border-b border-gray-200" data-id="element-4262">
          <div className="flex items-center justify-between" data-id="element-4263">
            <label htmlFor="documentType" className="block text-sm font-medium text-gray-700" data-id="element-4264">
              Document Classification
            </label>
            {renderConfidenceIndicator(extractedData.confidence.documentType)}
          </div>
          <select id="documentType" name="documentType" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={documentType} onChange={e => handleDocumentTypeChange(e.target.value)} data-id="element-4265">
            <option value="PO Invoice" data-id="element-4266">PO Invoice</option>
            <option value="Non-PO Invoice" data-id="element-4267">Non-PO Invoice</option>
            <option value="Credit Note" data-id="element-4268">Credit Note</option>
          </select>
        </div>
        {/* Header Information Section */}
        <div className="px-4 py-4 border-b border-gray-200" data-id="element-4269">
          <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleSection('header')} data-id="element-4270">
            <h4 className="text-sm font-medium text-gray-900" data-id="element-4271">
              Header Information
            </h4>
            {expandedSections.header ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-4272" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-4273" />}
          </div>
          {expandedSections.header && <div className="mt-3 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2" data-id="element-4274">
              <div data-id="element-4275">
                <div className="flex items-center justify-between" data-id="element-4276">
                  <label htmlFor="invoiceNumber" className="block text-sm font-medium text-gray-700" data-id="element-4277">
                    {documentType === 'Credit Note' ? 'Credit Note Number' : 'Invoice Number'}
                  </label>
                  {renderConfidenceIndicator(extractedData.confidence.invoiceNumber)}
                </div>
                <input type="text" id="invoiceNumber" name="invoiceNumber" value={extractedData.header.invoiceNumber} onChange={e => handleFieldUpdate('header', 'invoiceNumber', e.target.value)} className={`mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${extractedData.confidence.invoiceNumber < 0.6 ? 'border-red-300 bg-red-50' : extractedData.confidence.invoiceNumber < 0.8 ? 'border-yellow-300 bg-yellow-50' : ''}`} data-id="element-4278" />
              </div>
              <div data-id="element-4279">
                <div className="flex items-center justify-between" data-id="element-4280">
                  <label htmlFor="invoiceDate" className="block text-sm font-medium text-gray-700" data-id="element-4281">
                    {documentType === 'Credit Note' ? 'Credit Note Date' : 'Invoice Date'}
                  </label>
                  {renderConfidenceIndicator(extractedData.confidence.invoiceDate)}
                </div>
                <input type="date" id="invoiceDate" name="invoiceDate" value={extractedData.header.invoiceDate} onChange={e => handleFieldUpdate('header', 'invoiceDate', e.target.value)} className={`mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${extractedData.confidence.invoiceDate < 0.6 ? 'border-red-300 bg-red-50' : extractedData.confidence.invoiceDate < 0.8 ? 'border-yellow-300 bg-yellow-50' : ''}`} data-id="element-4282" />
              </div>
              {documentType !== 'Credit Note' && <div data-id="element-4283">
                  <div className="flex items-center justify-between" data-id="element-4284">
                    <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700" data-id="element-4285">
                      Due Date
                    </label>
                    {renderConfidenceIndicator(extractedData.confidence.dueDate)}
                  </div>
                  <input type="date" id="dueDate" name="dueDate" value={extractedData.header.dueDate} onChange={e => handleFieldUpdate('header', 'dueDate', e.target.value)} className={`mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${extractedData.confidence.dueDate < 0.6 ? 'border-red-300 bg-red-50' : extractedData.confidence.dueDate < 0.8 ? 'border-yellow-300 bg-yellow-50' : ''}`} data-id="element-4286" />
                </div>}
              {documentType === 'PO Invoice' && <div data-id="element-4287">
                  <div className="flex items-center justify-between" data-id="element-4288">
                    <label htmlFor="poReference" className="block text-sm font-medium text-gray-700" data-id="element-4289">
                      PO Reference
                    </label>
                    {renderConfidenceIndicator(extractedData.confidence.poReference)}
                  </div>
                  <input type="text" id="poReference" name="poReference" value={extractedData.header.poReference} onChange={e => handleFieldUpdate('header', 'poReference', e.target.value)} className={`mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${extractedData.confidence.poReference < 0.6 ? 'border-red-300 bg-red-50' : extractedData.confidence.poReference < 0.8 ? 'border-yellow-300 bg-yellow-50' : ''}`} data-id="element-4290" />
                </div>}
              <div data-id="element-4291">
                <div className="flex items-center justify-between" data-id="element-4292">
                  <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-4293">
                    Currency
                  </label>
                  {renderConfidenceIndicator(extractedData.confidence.currency)}
                </div>
                <select id="currency" name="currency" value={extractedData.header.currency} onChange={e => handleFieldUpdate('header', 'currency', e.target.value)} className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md ${extractedData.confidence.currency < 0.6 ? 'border-red-300 bg-red-50' : extractedData.confidence.currency < 0.8 ? 'border-yellow-300 bg-yellow-50' : ''}`} data-id="element-4294">
                  <option value="USD" data-id="element-4295">USD - US Dollar</option>
                  <option value="EUR" data-id="element-4296">EUR - Euro</option>
                  <option value="GBP" data-id="element-4297">GBP - British Pound</option>
                  <option value="CAD" data-id="element-4298">CAD - Canadian Dollar</option>
                  <option value="AUD" data-id="element-4299">AUD - Australian Dollar</option>
                  <option value="JPY" data-id="element-4300">JPY - Japanese Yen</option>
                </select>
              </div>
              <div data-id="element-4301">
                <div className="flex items-center justify-between" data-id="element-4302">
                  <label htmlFor="total" className="block text-sm font-medium text-gray-700" data-id="element-4303">
                    Total Amount
                  </label>
                  {renderConfidenceIndicator(extractedData.confidence.total)}
                </div>
                <input type="number" id="total" name="total" step="0.01" value={extractedData.header.total} onChange={e => handleFieldUpdate('header', 'total', parseFloat(e.target.value))} className={`mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${extractedData.confidence.total < 0.6 ? 'border-red-300 bg-red-50' : extractedData.confidence.total < 0.8 ? 'border-yellow-300 bg-yellow-50' : ''}`} data-id="element-4304" />
              </div>
              <div data-id="element-4305">
                <div className="flex items-center justify-between" data-id="element-4306">
                  <label htmlFor="tax" className="block text-sm font-medium text-gray-700" data-id="element-4307">
                    Tax Amount
                  </label>
                  {renderConfidenceIndicator(extractedData.confidence.tax)}
                </div>
                <input type="number" id="tax" name="tax" step="0.01" value={extractedData.header.tax} onChange={e => handleFieldUpdate('header', 'tax', parseFloat(e.target.value))} className={`mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${extractedData.confidence.tax < 0.6 ? 'border-red-300 bg-red-50' : extractedData.confidence.tax < 0.8 ? 'border-yellow-300 bg-yellow-50' : ''}`} data-id="element-4308" />
              </div>
            </div>}
        </div>
        {/* Vendor Information Section */}
        <div className="px-4 py-4 border-b border-gray-200" data-id="element-4309">
          <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleSection('vendor')} data-id="element-4310">
            <h4 className="text-sm font-medium text-gray-900" data-id="element-4311">
              Vendor Information
            </h4>
            {expandedSections.vendor ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-4312" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-4313" />}
          </div>
          {expandedSections.vendor && <div className="mt-3" data-id="element-4314">
              <div className="flex items-center justify-between mb-4" data-id="element-4315">
                <div className="flex-grow mr-2" data-id="element-4316">
                  <div className="flex items-center justify-between" data-id="element-4317">
                    <label htmlFor="vendorName" className="block text-sm font-medium text-gray-700" data-id="element-4318">
                      Vendor
                    </label>
                    {renderConfidenceIndicator(extractedData.confidence.vendorName)}
                  </div>
                  <div className="mt-1 relative rounded-md shadow-sm" data-id="element-4319">
                    <input type="text" id="vendorName" name="vendorName" value={extractedData.vendor.name} readOnly className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md bg-gray-50" data-id="element-4320" />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center" data-id="element-4321">
                      <button type="button" onClick={() => setIsVendorLookupOpen(true)} className="text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-4322">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" data-id="element-4323">
                          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" data-id="element-4324" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="w-1/3" data-id="element-4325">
                  <label htmlFor="vendorId" className="block text-sm font-medium text-gray-700" data-id="element-4326">
                    Vendor ID
                  </label>
                  <input type="text" id="vendorId" name="vendorId" value={extractedData.vendor.id} readOnly className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md bg-gray-50" data-id="element-4327" />
                </div>
              </div>
              <div className="mb-4" data-id="element-4328">
                <label htmlFor="vendorAddress" className="block text-sm font-medium text-gray-700" data-id="element-4329">
                  Vendor Address
                </label>
                <textarea id="vendorAddress" name="vendorAddress" rows={2} value={extractedData.vendor.address} readOnly className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md bg-gray-50" data-id="element-4330" />
              </div>
              <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2" data-id="element-4331">
                <div data-id="element-4332">
                  <label htmlFor="vendorTaxId" className="block text-sm font-medium text-gray-700" data-id="element-4333">
                    Tax ID
                  </label>
                  <input type="text" id="vendorTaxId" name="vendorTaxId" value={extractedData.vendor.taxId} readOnly className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md bg-gray-50" data-id="element-4334" />
                </div>
                <div data-id="element-4335">
                  <label htmlFor="vendorEmail" className="block text-sm font-medium text-gray-700" data-id="element-4336">
                    Email
                  </label>
                  <input type="email" id="vendorEmail" name="vendorEmail" value={extractedData.vendor.email} readOnly className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md bg-gray-50" data-id="element-4337" />
                </div>
              </div>
              {/* Add Bank Details Verification Section */}
              <div className="mt-4 border-t border-gray-200 pt-4" data-id="element-4338">
                <div className="flex items-center justify-between mb-3" data-id="element-4339">
                  <h5 className="text-sm font-medium text-gray-700" data-id="element-4340">
                    Invoice Bank Details Verification
                  </h5>
                  <div data-id="element-4341">
                    {extractedData.bankDetails?.verified === true ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-4342">
                        <ThumbsUpIcon className="mr-1 h-4 w-4 text-green-500" data-id="element-4343" />
                        EFTSure Verified
                      </span> : extractedData.bankDetails?.verified === false ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-4344">
                        <ThumbsDownIcon className="mr-1 h-4 w-4 text-red-500" data-id="element-4345" />
                        EFTSure Mismatch
                      </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-4346">
                        <ClockIcon className="mr-1 h-4 w-4" data-id="element-4347" />
                        Verification Pending
                      </span>}
                  </div>
                </div>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-3" data-id="element-4348">
                  <div className="flex" data-id="element-4349">
                    <div className="flex-shrink-0" data-id="element-4350">
                      <AlertTriangleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" data-id="element-4351" />
                    </div>
                    <div className="ml-3" data-id="element-4352">
                      <p className="text-xs text-yellow-700" data-id="element-4353">
                        Always verify bank details before processing payment.
                        Bank fraud is a common occurrence.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-4354">
                  {/* Extracted Bank Details */}
                  <div className="border border-gray-200 rounded-md p-3 bg-white" data-id="element-4355">
                    <h6 className="text-xs font-medium text-gray-500 mb-2" data-id="element-4356">
                      Extracted from Invoice
                    </h6>
                    <dl className="space-y-1" data-id="element-4357">
                      <div className="flex justify-between" data-id="element-4358">
                        <dt className="text-xs font-medium text-gray-500" data-id="element-4359">
                          Account Name
                        </dt>
                        <dd className={`text-xs ${extractedData.bankDetails?.mismatchFields?.includes('accountName') ? 'text-red-600 font-medium' : 'text-gray-900'}`} data-id="element-4360">
                          PrintWorks Inc.
                          {extractedData.bankDetails?.mismatchFields?.includes('accountName') && <AlertTriangleIcon className="inline-block h-3 w-3 ml-1 text-red-500" data-id="element-4361" />}
                        </dd>
                      </div>
                      <div className="flex justify-between" data-id="element-4362">
                        <dt className="text-xs font-medium text-gray-500" data-id="element-4363">
                          Account Number
                        </dt>
                        <dd className={`text-xs ${extractedData.bankDetails?.mismatchFields?.includes('accountNumber') ? 'text-red-600 font-medium' : 'text-gray-900'}`} data-id="element-4364">
                          4567•••••0123
                          {extractedData.bankDetails?.mismatchFields?.includes('accountNumber') && <AlertTriangleIcon className="inline-block h-3 w-3 ml-1 text-red-500" data-id="element-4365" />}
                        </dd>
                      </div>
                      <div className="flex justify-between" data-id="element-4366">
                        <dt className="text-xs font-medium text-gray-500" data-id="element-4367">
                          Routing Number
                        </dt>
                        <dd className={`text-xs ${extractedData.bankDetails?.mismatchFields?.includes('routingNumber') ? 'text-red-600 font-medium' : 'text-gray-900'}`} data-id="element-4368">
                          021000021
                          {extractedData.bankDetails?.mismatchFields?.includes('routingNumber') && <AlertTriangleIcon className="inline-block h-3 w-3 ml-1 text-red-500" data-id="element-4369" />}
                        </dd>
                      </div>
                      <div className="flex justify-between" data-id="element-4370">
                        <dt className="text-xs font-medium text-gray-500" data-id="element-4371">
                          Bank Name
                        </dt>
                        <dd className="text-xs text-gray-900" data-id="element-4372">Chase Bank</dd>
                      </div>
                    </dl>
                    <div className="mt-2 text-xs text-gray-500" data-id="element-4373">
                      <FileTextIcon className="inline-block h-3 w-3 mr-1" data-id="element-4374" />
                      <span data-id="element-4375">Extracted from: INV-2023-4501</span>
                    </div>
                  </div>
                  {/* EFTSure Verified Details */}
                  <div className="border border-gray-200 rounded-md p-3 bg-white" data-id="element-4376">
                    <h6 className="text-xs font-medium text-gray-500 mb-2" data-id="element-4377">
                      Vendor Record (EFTSure)
                    </h6>
                    <dl className="space-y-1" data-id="element-4378">
                      <div className="flex justify-between" data-id="element-4379">
                        <dt className="text-xs font-medium text-gray-500" data-id="element-4380">
                          Account Name
                        </dt>
                        <dd className={`text-xs ${extractedData.bankDetails?.mismatchFields?.includes('accountName') ? 'text-red-600 font-medium' : 'text-gray-900'}`} data-id="element-4381">
                          PrintWorks Incorporated
                        </dd>
                      </div>
                      <div className="flex justify-between" data-id="element-4382">
                        <dt className="text-xs font-medium text-gray-500" data-id="element-4383">
                          Account Number
                        </dt>
                        <dd className={`text-xs ${extractedData.bankDetails?.mismatchFields?.includes('accountNumber') ? 'text-red-600 font-medium' : 'text-gray-900'}`} data-id="element-4384">
                          9876•••••5432
                        </dd>
                      </div>
                      <div className="flex justify-between" data-id="element-4385">
                        <dt className="text-xs font-medium text-gray-500" data-id="element-4386">
                          Routing Number
                        </dt>
                        <dd className={`text-xs ${extractedData.bankDetails?.mismatchFields?.includes('routingNumber') ? 'text-red-600 font-medium' : 'text-gray-900'}`} data-id="element-4387">
                          021000021
                        </dd>
                      </div>
                      <div className="flex justify-between" data-id="element-4388">
                        <dt className="text-xs font-medium text-gray-500" data-id="element-4389">
                          Bank Name
                        </dt>
                        <dd className="text-xs text-gray-900" data-id="element-4390">Chase Bank</dd>
                      </div>
                    </dl>
                    <div className="mt-2 flex items-center text-xs" data-id="element-4391">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-4392">
                        <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-4393" />
                        Verified
                      </span>
                      <span className="ml-2 text-gray-500" data-id="element-4394">
                        EFTSure • 2023-09-15
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex justify-end" data-id="element-4395">
                  {extractedData.bankDetails?.verified === true ? <button type="button" className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-4396">
                      <CheckIcon className="h-3 w-3 mr-1" data-id="element-4397" />
                      Approve Bank Details
                    </button> : <button type="button" className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-4398">
                      <AlertTriangleIcon className="h-3 w-3 mr-1" data-id="element-4399" />
                      Resolve Bank Detail Issues
                    </button>}
                </div>
              </div>
            </div>}
        </div>
        {/* Entity and Site Information Section */}
        <div className="px-4 py-4 border-b border-gray-200" data-id="element-4400">
          <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleSection('entitySite')} data-id="element-4401">
            <h4 className="text-sm font-medium text-gray-900" data-id="element-4402">
              Organisation Unit Information
            </h4>
            {expandedSections.entitySite ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-4403" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-4404" />}
          </div>
          {expandedSections.entitySite && <div className="mt-3" data-id="element-4405">
              <div className="mb-4" data-id="element-4406">
                <div className="flex items-center justify-between" data-id="element-4407">
                  <label htmlFor="orgUnit" className="block text-sm font-medium text-gray-700" data-id="element-4408">
                    Organisation Unit
                  </label>
                  {renderConfidenceIndicator(extractedData.confidence.orgUnitName)}
                </div>
                <div className="mt-1" data-id="element-4409">
                  <OrgUnitSelector value={extractedData.orgUnit.orgUnitId} onChange={(orgUnitId, orgUnit) => {
                setExtractedData({
                  ...extractedData,
                  orgUnit: {
                    orgUnitId,
                    orgUnitName: orgUnit?.name || '',
                    orgUnitPath: orgUnit?.path || ''
                  }
                });
                setIsDirty(true);
              }} label="" showPath={true} data-id="element-4410" />
                </div>
              </div>
              {extractedData.orgUnit.orgUnitPath && <div className="mb-4" data-id="element-4411">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-4412">
                    Organisation Path
                  </label>
                  <div className="mt-1 text-sm text-gray-500 bg-gray-50 p-2 rounded-md" data-id="element-4413">
                    {extractedData.orgUnit.orgUnitPath}
                  </div>
                </div>}
            </div>}
        </div>
      </div>;
  };
  // Handle vendor select
  const handleVendorSelect = vendor => {
    // Update the extractedData with the selected vendor information
    setExtractedData({
      ...extractedData,
      vendor: {
        id: vendor.id,
        name: vendor.name,
        address: vendor.address,
        taxId: vendor.taxId,
        email: vendor.email
      }
    });
    setIsVendorLookupOpen(false);
  };
  // Handle entity select
  const handleEntitySelect = entity => {
    // Update the extractedData with the selected entity information
    setExtractedData({
      ...extractedData,
      entitySite: {
        ...extractedData.entitySite,
        entityId: entity.id,
        entityName: entity.name,
        entityDescription: entity.description
      }
    });
    setIsEntityLookupOpen(false);
    setIsDirty(true);
  };
  // Handle site select
  const handleSiteSelect = site => {
    // Update the extractedData with the selected site information
    setExtractedData({
      ...extractedData,
      entitySite: {
        ...extractedData.entitySite,
        siteId: site.id,
        siteName: site.name,
        siteAddress: site.address
      }
    });
    setIsSiteLookupOpen(false);
    setIsDirty(true);
  };
  return <div className="bg-white shadow-sm rounded-lg" data-id="element-4414">
      <div className="flex justify-between items-center mb-6" data-id="element-4415">
        <div data-id="element-4416">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-4417">Document Review</h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-4418">
            Review, correct, and process documents that require manual
            intervention
          </p>
        </div>
        <div className="flex space-x-2" data-id="element-4419">
          {selectedException && <>
              <button onClick={handleReExtract} disabled={isLoading} className={`inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-4420">
                <RotateCwIcon className="h-4 w-4 mr-2" data-id="element-4421" />
                Re-extract
              </button>
              <button onClick={handleSaveDraft} disabled={isLoading || !isDirty} className={`inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${isLoading || !isDirty ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-4422">
                <SaveIcon className="h-4 w-4 mr-2" data-id="element-4423" />
                Save Draft
              </button>
              <button onClick={handleFlagException} disabled={isLoading} className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-4424">
                <FlagIcon className="h-4 w-4 mr-2" data-id="element-4425" />
                Flag Exception
              </button>
              <button onClick={handleValidate} disabled={isLoading} className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-4426">
                <CheckCircleIcon className="h-4 w-4 mr-2" data-id="element-4427" />
                Validate
              </button>
            </>}
        </div>
      </div>
      {isLoading && <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50" data-id="element-4428">
          <div className="bg-white p-4 rounded-lg shadow-lg flex items-center" data-id="element-4429">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mr-3" data-id="element-4430"></div>
            <p data-id="element-4431">Processing...</p>
          </div>
        </div>}
      {/* Modified layout structure */}
      <div className="flex h-full gap-6" data-id="element-4432">
        {/* Left Column - Exception Queue (fixed width) */}
        <div className="w-1/4" data-id="element-4433">
          <ExceptionQueue exceptions={filteredExceptions} selectedException={selectedException} onSelectException={handleSelectException} statusFilter={statusFilter} typeFilter={typeFilter} onStatusFilterChange={setStatusFilter} onTypeFilterChange={setTypeFilter} onMovePage={handleMovePage} onSplitDocument={handleSplitDocument} data-id="element-4434" />
        </div>
        {/* Right Column - Main Workspace */}
        <div className="w-3/4" data-id="element-4435">
          {selectedException ? <div className="grid grid-cols-2 gap-6" data-id="element-4436">
              {/* Left Column - Header & Vendor Data (now extends down) */}
              <div className="flex flex-col" data-id="element-4437">
                {renderHeaderAndVendorInfo()}
                {/* Add space to extend this column down */}
                <div className="flex-grow mt-6" data-id="element-4438"></div>
              </div>
              {/* Right Column - Document Viewer */}
              <div data-id="element-4439">
                <DocumentViewer documentUrl={documentImage} extractedData={extractedData} isExceptionMode={true} data-id="element-4440" />
                {/* Page Thumbnails for Document Viewer */}
                {selectedException.pages && selectedException.pages.length > 1 && <div className="mt-2 bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-4441">
                      <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 overflow-x-auto" data-id="element-4442">
                        <div className="flex space-x-2" data-id="element-4443">
                          {selectedException.pages.map((page, index) => <div key={index} className={`relative flex-shrink-0 w-16 h-20 border-2 ${currentPageIndex === index ? 'border-indigo-500' : 'border-gray-300'} rounded overflow-hidden cursor-pointer`} onClick={() => handlePageChange(index)} data-id="element-4444">
                              <img src={page.thumbnail} alt={`Page ${index + 1}`} className="w-full h-full object-cover" data-id="element-4445" />
                              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs py-1 text-center" data-id="element-4446">
                                {index + 1}
                              </div>
                            </div>)}
                          <div className="flex-shrink-0 w-16 h-20 border border-gray-300 rounded overflow-hidden flex items-center justify-center bg-gray-50 cursor-pointer" data-id="element-4447">
                            <PlusIcon className="h-6 w-6 text-gray-400" data-id="element-4448" />
                          </div>
                        </div>
                      </div>
                    </div>}
              </div>
              {/* Row 2: Full-width Line Items & Matching (now spans across both columns) */}
              <div className="col-span-2" data-id="element-4449">
                {documentType === 'PO Invoice' && renderPOInvoiceLineItems()}
                {documentType === 'Non-PO Invoice' && renderNonPOInvoiceLineItems()}
                {documentType === 'Credit Note' && renderCreditNoteLineItems()}
              </div>
            </div> : <div className="bg-white shadow overflow-hidden sm:rounded-lg h-64 flex items-center justify-center" data-id="element-4450">
              <div className="text-center" data-id="element-4451">
                <CornerDownRightIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-4452" />
                <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-4453">
                  No document selected
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-4454">
                  Select a document from the exception queue to begin processing
                </p>
              </div>
            </div>}
        </div>
      </div>
      {/* Vendor Lookup Modal */}
      <VendorLookupModal isOpen={isVendorLookupOpen} onClose={() => setIsVendorLookupOpen(false)} onSelectVendor={handleVendorSelect} data-id="element-4455" />
    </div>;
}